import { useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useApp } from '@/state/AppContext';
import { Meldingen } from '@/components/ui/Feedback';
import { DashboardIcoon, GeenVerbindingIcoon, KaartIcoon } from './Iconen';
import { Merknaam } from './Merk';
import { adresRegel, relatieveTijd } from '@/utils/format';
import './layout.css';

/** Kopbalk, tabnavigatie en meldingen rondom de pagina-inhoud. */
export function AppSchil({ children }: { children: ReactNode }): JSX.Element {
  const { online, wachtrijAantal, wachtrij, gebruiker, uitloggen, alleenLezen } = useApp();
  const [wachtrijOpen, setWachtrijOpen] = useState(false);

  const tabKlasse = (actief: boolean, basis: string) =>
    `${basis}${actief ? ` ${basis}--actief` : ''}`;

  return (
    <div className="schil">
      <header className="kopbalk">
        <div className="kopbalk__merk">
          <Merknaam diapositief formaat={28} />
        </div>

        <nav className="kopbalk__tabs" aria-label="Hoofdnavigatie">
          <NavLink to="/" className={({ isActive }) => tabKlasse(isActive, 'kopbalk__tab')} end>
            <KaartIcoon /> Kaart
          </NavLink>
          <NavLink
            to="/dashboard"
            className={({ isActive }) => tabKlasse(isActive, 'kopbalk__tab')}
          >
            <DashboardIcoon /> Dashboard
          </NavLink>
        </nav>

        <div className="kopbalk__rechts">
          {alleenLezen && (
            <span className="rolbadge" title="Je kijkt mee en kunt niets wijzigen">
              Meekijken
            </span>
          )}

          {/* Uitklapbaar: het getal zegt dát er iets wacht, de lijst zegt wát.
              Zonder die lijst blijft een coach zich afvragen of zijn bezoek van
              vanmorgen er wel bij zit. */}
          {wachtrijAantal > 0 && (
            <button
              type="button"
              className="wachtrij-teller"
              onClick={() => setWachtrijOpen((huidig) => !huidig)}
              aria-expanded={wachtrijOpen}
              aria-label={`${wachtrijAantal} registraties wachten op verzending`}
            >
              <GeenVerbindingIcoon />
              {wachtrijAantal}
            </button>
          )}

          <span
            className={`verbindingsstatus${online ? '' : ' verbindingsstatus--offline'}`}
            title={
              online
                ? 'Verbonden'
                : 'Geen verbinding. Alles wat je invult staat op dit toestel en gaat vanzelf mee zodra er bereik is.'
            }
          >
            <span className="verbindingsstatus__stip" />
            {online ? 'Online' : 'Geen bereik'}
          </span>

          {gebruiker && (
            <div className="kopbalk__gebruiker">
              <span className="kopbalk__naam" title={gebruiker.email}>
                {gebruiker.naam}
              </span>
              <button
                type="button"
                className="kopbalk__uitloggen"
                onClick={() => void uitloggen()}
              >
                Uitloggen
              </button>
            </div>
          )}
        </div>
      </header>

      {wachtrijOpen && wachtrijAantal > 0 && (
        <div className="wachtrij" role="dialog" aria-label="Nog te versturen">
          <div className="wachtrij__kop">Nog te versturen</div>
          <p className="wachtrij__uitleg">
            Alles staat veilig op dit toestel. Zodra er bereik is, gaat het automatisch mee.
          </p>
          <ul className="wachtrij__lijst">
            {wachtrij.map((item) => (
              <li key={item.registratie.id} className="wachtrij__item">
                <span className="wachtrij__adres">
                  {adresRegel(item.registratie.adres) || 'Handmatig punt'}
                </span>
                <span className="wachtrij__meta">
                  {item.registratie.contactmomenten[item.registratie.contactmomenten.length - 1]?.soort ?? "Registratie"} ·{" "}
                  {relatieveTijd(item.toegevoegdOp)}
                  {item.pogingen > 0 && ` · ${item.pogingen} pogingen`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <main className="schil__inhoud">{children}</main>

      <nav className="onderbalk" aria-label="Hoofdnavigatie">
        <NavLink to="/" className={({ isActive }) => tabKlasse(isActive, 'onderbalk__tab')} end>
          <KaartIcoon />
          Kaart
        </NavLink>
        <NavLink
          to="/dashboard"
          className={({ isActive }) => tabKlasse(isActive, 'onderbalk__tab')}
        >
          <DashboardIcoon />
          Dashboard
        </NavLink>
      </nav>

      <Meldingen />
    </div>
  );
}

/** Regendruppel op een dak: het thema van de app in één vorm. */
